// ============================================
// COMPONENTE: Gráfico de Intensidade de Sintomas
// ============================================
// Exibe a evolução da intensidade média dos sintomas ao longo do tempo
// ============================================

import { useState } from 'react';
import {
  LineChart, Line,
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface DadoEvolucao {
  data: string;
  prontuario_id: number;
  totalSintomas: number;
  totalQueixas: number;
  totalHumor: number;
  mediaIntensidade?: number;
}

interface Props {
  dados: DadoEvolucao[];
}

type TipoVisualizacao = 'linha' | 'area' | 'barras';

export default function GraficoIntensidade({ dados }: Props) {
  const [tipoGrafico, setTipoGrafico] = useState<TipoVisualizacao>('linha');
  const [mostrarMetricas, setMostrarMetricas] = useState({
    sintomas: true,
    queixas: true,
    humor: true,
    intensidade: true
  });

  if (!dados || dados.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-chart-area me-2"></i>
            Evolução por Sessão
          </h3>
        </div>
        <div className="card-body">
          <p className="text-muted text-center py-4">
            <i className="fas fa-chart-line fa-3x mb-3 d-block text-secondary"></i>
            Nenhum dado de evolução disponível
          </p>
        </div>
      </div>
    );
  }

  // Formatar dados
  const dadosFormatados = dados.map((d, idx) => ({
    ...d,
    sessao: `Sessão ${idx + 1}`,
    dataFormatada: new Date(d.data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    }),
    mediaIntensidade: d.mediaIntensidade || 0
  }));

  // Calcular médias gerais
  const mediaGeral = {
    sintomas: dados.reduce((sum, d) => sum + d.totalSintomas, 0) / dados.length,
    queixas: dados.reduce((sum, d) => sum + d.totalQueixas, 0) / dados.length,
    humor: dados.reduce((sum, d) => sum + d.totalHumor, 0) / dados.length,
    intensidade: dados.reduce((sum, d) => sum + (d.mediaIntensidade || 0), 0) / dados.length
  };

  // Cores para cada métrica
  const cores = {
    sintomas: '#dc3545',
    queixas: '#ffc107',
    humor: '#0d6efd',
    intensidade: '#6f42c1'
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border rounded p-2 shadow-sm">
          <p className="mb-1"><strong>{data.sessao}</strong></p>
          <p className="mb-0 text-muted small">{data.dataFormatada}</p>
          <hr className="my-1" />
          {mostrarMetricas.sintomas && (
            <p className="mb-0" style={{ color: cores.sintomas }}>
              <i className="fas fa-heartbeat me-1"></i>
              Sintomas: {data.totalSintomas}
            </p>
          )}
          {mostrarMetricas.queixas && (
            <p className="mb-0" style={{ color: cores.queixas }}>
              <i className="fas fa-comment-medical me-1"></i>
              Queixas: {data.totalQueixas}
            </p>
          )}
          {mostrarMetricas.humor && (
            <p className="mb-0" style={{ color: cores.humor }}>
              <i className="fas fa-smile me-1"></i>
              Humor: {data.totalHumor}
            </p>
          )}
          {mostrarMetricas.intensidade && data.mediaIntensidade > 0 && (
            <p className="mb-0" style={{ color: cores.intensidade }}>
              <i className="fas fa-thermometer-half me-1"></i>
              Intensidade Média: {data.mediaIntensidade.toFixed(1)}/10
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderGrafico = () => {
    const props = {
      data: dadosFormatados,
      margin: { top: 20, right: 30, left: 20, bottom: 60 }
    };

    const xAxisProps = {
      dataKey: "sessao",
      angle: -45,
      textAnchor: "end" as const,
      height: 80,
      interval: Math.max(0, Math.ceil(dadosFormatados.length / 10) - 1),
      tick: { fontSize: 11 }
    };

    switch (tipoGrafico) {
      case 'area':
        return (
          <AreaChart {...props}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {mostrarMetricas.sintomas && (
              <Area type="monotone" dataKey="totalSintomas" stroke={cores.sintomas} fill={cores.sintomas} fillOpacity={0.2} name="Sintomas" />
            )}
            {mostrarMetricas.queixas && (
              <Area type="monotone" dataKey="totalQueixas" stroke={cores.queixas} fill={cores.queixas} fillOpacity={0.2} name="Queixas" />
            )}
            {mostrarMetricas.humor && (
              <Area type="monotone" dataKey="totalHumor" stroke={cores.humor} fill={cores.humor} fillOpacity={0.2} name="Humor" />
            )}
          </AreaChart>
        );

      case 'barras':
        return (
          <BarChart {...props}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {mostrarMetricas.sintomas && (
              <Bar dataKey="totalSintomas" fill={cores.sintomas} name="Sintomas" />
            )}
            {mostrarMetricas.queixas && (
              <Bar dataKey="totalQueixas" fill={cores.queixas} name="Queixas" />
            )}
            {mostrarMetricas.humor && (
              <Bar dataKey="totalHumor" fill={cores.humor} name="Humor" />
            )}
          </BarChart>
        );

      default: // linha
        return (
          <LineChart {...props}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 10]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {mostrarMetricas.sintomas && (
              <Line yAxisId="left" type="monotone" dataKey="totalSintomas" stroke={cores.sintomas} strokeWidth={2} name="Sintomas" dot={{ r: 4 }} />
            )}
            {mostrarMetricas.queixas && (
              <Line yAxisId="left" type="monotone" dataKey="totalQueixas" stroke={cores.queixas} strokeWidth={2} name="Queixas" dot={{ r: 4 }} />
            )}
            {mostrarMetricas.humor && (
              <Line yAxisId="left" type="monotone" dataKey="totalHumor" stroke={cores.humor} strokeWidth={2} name="Humor" dot={{ r: 4 }} />
            )}
            {mostrarMetricas.intensidade && dadosFormatados.some(d => d.mediaIntensidade > 0) && (
              <>
                <Line yAxisId="right" type="monotone" dataKey="mediaIntensidade" stroke={cores.intensidade} strokeWidth={3} strokeDasharray="5 5" name="Intensidade (1-10)" dot={{ r: 5 }} />
                <ReferenceLine yAxisId="right" y={5} stroke="#999" strokeDasharray="3 3" label={{ value: 'Média', position: 'right' }} />
              </>
            )}
          </LineChart>
        );
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h3 className="card-title mb-0">
            <i className="fas fa-chart-area me-2"></i>
            Evolução por Sessão
          </h3>
          <div className="btn-group btn-group-sm">
            <button
              className={`btn ${tipoGrafico === 'linha' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTipoGrafico('linha')}
              title="Gráfico de Linha"
            >
              <i className="fas fa-chart-line"></i>
            </button>
            <button
              className={`btn ${tipoGrafico === 'area' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTipoGrafico('area')}
              title="Gráfico de Área"
            >
              <i className="fas fa-mountain"></i>
            </button>
            <button
              className={`btn ${tipoGrafico === 'barras' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTipoGrafico('barras')}
              title="Gráfico de Barras"
            >
              <i className="fas fa-chart-bar"></i>
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        {/* Filtros de métricas */}
        <div className="d-flex flex-wrap gap-3 mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="checkSintomas"
              checked={mostrarMetricas.sintomas}
              onChange={(e) => setMostrarMetricas(prev => ({ ...prev, sintomas: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="checkSintomas" style={{ color: cores.sintomas }}>
              <i className="fas fa-heartbeat me-1"></i>
              Sintomas
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="checkQueixas"
              checked={mostrarMetricas.queixas}
              onChange={(e) => setMostrarMetricas(prev => ({ ...prev, queixas: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="checkQueixas" style={{ color: cores.queixas }}>
              <i className="fas fa-comment-medical me-1"></i>
              Queixas
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="checkHumor"
              checked={mostrarMetricas.humor}
              onChange={(e) => setMostrarMetricas(prev => ({ ...prev, humor: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="checkHumor" style={{ color: cores.humor }}>
              <i className="fas fa-smile me-1"></i>
              Humor
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="checkIntensidade"
              checked={mostrarMetricas.intensidade}
              onChange={(e) => setMostrarMetricas(prev => ({ ...prev, intensidade: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="checkIntensidade" style={{ color: cores.intensidade }}>
              <i className="fas fa-thermometer-half me-1"></i>
              Intensidade
            </label>
          </div>
        </div>

        {/* Gráfico */}
        <ResponsiveContainer width="100%" height={400}>
          {renderGrafico()}
        </ResponsiveContainer>

        {/* Estatísticas resumidas */}
        <div className="row mt-3 pt-3 border-top">
          <div className="col-3 text-center">
            <small className="text-muted d-block">Média Sintomas</small>
            <strong style={{ color: cores.sintomas }}>{mediaGeral.sintomas.toFixed(1)}</strong>
          </div>
          <div className="col-3 text-center">
            <small className="text-muted d-block">Média Queixas</small>
            <strong style={{ color: cores.queixas }}>{mediaGeral.queixas.toFixed(1)}</strong>
          </div>
          <div className="col-3 text-center">
            <small className="text-muted d-block">Média Humor</small>
            <strong style={{ color: cores.humor }}>{mediaGeral.humor.toFixed(1)}</strong>
          </div>
          <div className="col-3 text-center">
            <small className="text-muted d-block">Intensidade Média</small>
            <strong style={{ color: cores.intensidade }}>{mediaGeral.intensidade.toFixed(1)}/10</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
